import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Textarea } from "@shared/ui/textarea";
import { Label } from "@shared/ui/label";
import { Checkbox } from "@shared/ui/checkbox";
import { Printer, Send, FileText, CheckCircle } from "lucide-react";
import { Account } from "@features/cx/outstanding/types";
import { useState } from "react";

interface SendStatementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  account: Account;
  monthlyBills: Array<{
    month: string;
    waterCharges: number;
    sewerCharges: number;
    lateFees: number;
    total: number;
    dueDate: string;
    status: string;
  }>;
  onStatementSent?: (statementDetails: any) => void;
}

export function SendStatementDialog({
  isOpen,
  onClose,
  account,
  monthlyBills,
  onStatementSent
}: SendStatementDialogProps) {
  const [includePaymentOptions, setIncludePaymentOptions] = useState(true);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [statementSent, setStatementSent] = useState(false);

  const totalOutstanding = monthlyBills.reduce((sum, bill) => sum + bill.total, 0);
  const customerEmail = `${account?.customerName?.toLowerCase()?.replace(' ', '.')?.trim()}@email.com`;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const handleSendStatement = () => {
    const statementDetails = {
      totalOutstanding,
      includePaymentOptions,
      additionalNotes,
      sentDate: new Date().toISOString()
    };
    
    setStatementSent(true);
    onStatementSent?.(statementDetails);
  };

  const handlePrintStatement = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Account Statement - ${account.accountNumber}</title>
            <style>
              body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; background-color: #f8f9fa; }
              .statement-container { max-width: 800px; margin: 20px auto; background: white; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
              .header { background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); color: white; padding: 30px; }
              .header h1 { margin: 0; font-size: 28px; font-weight: 300; }
              .header .utility-info { font-size: 14px; margin-top: 5px; opacity: 0.9; }
              .statement-info { background: #f1f5f9; padding: 15px 30px; border-bottom: 1px solid #e2e8f0; }
              .statement-info table { width: 100%; border-collapse: collapse; }
              .statement-info td { padding: 5px 0; vertical-align: top; }
              .statement-info .label { color: #64748b; font-weight: 500; width: 150px; }
              .content { padding: 30px; }
              .section { margin-bottom: 30px; }
              .section-title { color: #1e293b; font-size: 18px; font-weight: 600; margin-bottom: 15px; border-bottom: 2px solid #e2e8f0; padding-bottom: 5px; }
              .transaction-table { width: 100%; border-collapse: collapse; margin: 15px 0; }
              .transaction-table th { background: #f8fafc; color: #374151; font-weight: 600; padding: 12px; text-align: left; border-bottom: 2px solid #e5e7eb; }
              .transaction-table td { padding: 12px; border-bottom: 1px solid #f3f4f6; }
              .transaction-table tr:hover { background: #f9fafb; }
              .total-row { background: #eff6ff !important; font-weight: 600; color: #1d4ed8; }
              .amount-positive { color: #059669; font-weight: 600; }
              .amount-negative { color: #dc2626; font-weight: 600; }
              .summary-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin: 20px 0; }
              .summary-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
              .summary-item { }
              .summary-label { color: #64748b; font-size: 14px; font-weight: 500; }
              .summary-value { font-size: 18px; font-weight: 600; color: #1e293b; }
              .footer { background: #f8fafc; padding: 20px 30px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b; }
              .contact-info { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 15px; }
              @media print { 
                body { margin: 0; background: white; } 
                .statement-container { box-shadow: none; margin: 0; }
              }
            </style>
          </head>
          <body>
            <div class="statement-container">
              <div class="header">
                <h1>ACCOUNT STATEMENT</h1>
                <div class="utility-info">Water & Sewer Services | Customer Service Portal</div>
              </div>
              
              <div class="statement-info">
                <table>
                  <tr>
                    <td class="label">Statement Date:</td>
                    <td><strong>${formatDate(new Date().toISOString())}</strong></td>
                    <td class="label">Account Number:</td>
                    <td><strong>${account.accountNumber}</strong></td>
                  </tr>
                  <tr>
                    <td class="label">Customer Name:</td>
                    <td>${account.customerName}</td>
                    <td class="label">Statement Period:</td>
                    <td>Outstanding Balances</td>
                  </tr>
                  <tr>
                    <td class="label">Service Address:</td>
                    <td colspan="3">${account.serviceAddress}</td>
                  </tr>
                </table>
              </div>

              <div class="content">
                <div class="summary-box">
                  <div class="summary-grid">
                    <div class="summary-item">
                      <div class="summary-label">Total Outstanding Balance</div>
                      <div class="summary-value amount-negative">${formatCurrency(totalOutstanding)}</div>
                    </div>
                    <div class="summary-item">
                      <div class="summary-label">Days Outstanding</div>
                      <div class="summary-value">${account.daysOutstanding} days</div>
                    </div>
                    <div class="summary-item">
                      <div class="summary-label">Last Payment</div>
                      <div class="summary-value">${formatCurrency(account.lastPaymentAmount)}</div>
                    </div>
                    <div class="summary-item">
                      <div class="summary-label">Payment Date</div>
                      <div class="summary-value">${formatDate(account.lastPaymentDate)}</div>
                    </div>
                  </div>
                </div>

                <div class="section">
                  <div class="section-title">Transaction History</div>
                  <table class="transaction-table">
                    <thead>
                      <tr>
                        <th>Transaction Date</th>
                        <th>Description</th>
                        <th>Due Date</th>
                        <th>Debits</th>
                        <th>Credits</th>
                        <th>Balance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${monthlyBills.map((bill, index) => {
                        const waterSewerTotal = bill.waterCharges + bill.sewerCharges;
                        const runningBalance = monthlyBills.slice(0, index + 1).reduce((sum, b) => sum + b.total, 0);
                        return `
                          <tr>
                            <td>${bill.month.split(' ')[0]} 1, ${new Date().getFullYear()}</td>
                            <td>Water & Sewer Services - ${bill.month}</td>
                            <td>${formatDate(bill.dueDate)}</td>
                            <td class="amount-negative">${formatCurrency(waterSewerTotal)}</td>
                            <td>-</td>
                            <td class="amount-negative">${formatCurrency(runningBalance - bill.lateFees)}</td>
                            <td>${bill.status}</td>
                          </tr>
                          ${bill.lateFees > 0 ? `
                          <tr>
                            <td>${new Date(bill.dueDate).toLocaleDateString()}</td>
                            <td>Late Fee - ${bill.month}</td>
                            <td>-</td>
                            <td class="amount-negative">${formatCurrency(bill.lateFees)}</td>
                            <td>-</td>
                            <td class="amount-negative">${formatCurrency(runningBalance)}</td>
                            <td>Applied</td>
                          </tr>
                          ` : ''}
                        `;
                      }).join('')}
                      <tr class="total-row">
                        <td colspan="3"><strong>TOTAL OUTSTANDING</strong></td>
                        <td><strong>${formatCurrency(totalOutstanding)}</strong></td>
                        <td><strong>-</strong></td>
                        <td><strong>${formatCurrency(totalOutstanding)}</strong></td>
                        <td><strong>Due</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                ${additionalNotes ? `
                <div class="section">
                  <div class="section-title">Important Notice</div>
                  <div style="background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; padding: 15px; color: #92400e;">
                    ${additionalNotes}
                  </div>
                </div>
                ` : ''}

                ${includePaymentOptions ? `
                <div class="section">
                  <div class="section-title">Payment Options</div>
                  <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px;">
                    <div>
                      <h4 style="color: #374151; margin-bottom: 10px;">Online & Phone</h4>
                      <ul style="margin: 0; padding-left: 20px; color: #4b5563;">
                        <li>Online: www.waterutility.com</li>
                        <li>Phone: (555) 123-4567</li>
                        <li>Mobile App: WaterPay</li>
                      </ul>
                    </div>
                    <div>
                      <h4 style="color: #374151; margin-bottom: 10px;">In-Person & Mail</h4>
                      <ul style="margin: 0; padding-left: 20px; color: #4b5563;">
                        <li>Office: 123 Utility Street</li>
                        <li>Drop Box: Available 24/7</li>
                        <li>Auto Pay: Set up recurring payments</li>
                      </ul>
                    </div>
                  </div>
                </div>
                ` : ''}
              </div>

              <div class="footer">
                <div><strong>Important:</strong> Please retain this statement for your records. Payment processing may take 2-3 business days.</div>
                <div class="contact-info">
                  <div><strong>Customer Service:</strong><br>(555) 123-4567</div>
                  <div><strong>Emergency Line:</strong><br>(555) 123-4568</div>
                  <div><strong>Office Hours:</strong><br>Mon-Fri: 8AM-5PM</div>
                </div>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  if (statementSent) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CheckCircle className="mr-2 h-5 w-5 text-green-600" />
              Statement Sent Successfully
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center py-6">
              <CheckCircle className="mx-auto h-16 w-16 text-green-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Statement Sent</h3>
              <p className="text-muted-foreground">
                Account statement has been sent to the customer for account {account.accountNumber}
              </p>
            </div>
            
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Statement Date:</span>
                    <p className="font-medium">{formatDate(new Date().toISOString())}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Outstanding:</span>
                    <p className="font-medium">{formatCurrency(totalOutstanding)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Bills Included:</span>
                    <p className="font-medium">{monthlyBills.length} months</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Payment Options:</span>
                    <p className="font-medium">{includePaymentOptions ? "Included" : "Not included"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex gap-2">
              <Button onClick={handlePrintStatement} className="flex-1">
                <Printer className="mr-2 h-4 w-4" />
                Print Statement
              </Button>
              <Button variant="outline" onClick={onClose} className="flex-1">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <FileText className="mr-2 h-5 w-5" />
            Send Account Statement
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-2">ACCOUNT STATEMENT</h3>
              <p className="text-sm text-muted-foreground mb-2">
                This statement will include all outstanding bills and account details.
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Email:</span>
                <span className="font-medium">{customerEmail}</span>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Account Details</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Account:</span>
                    <span>{account.accountNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Customer:</span>
                    <span>{account.customerName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type:</span>
                    <span>{account.customerType}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-2">Statement Summary</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Outstanding:</span>
                    <span className="font-bold">{formatCurrency(totalOutstanding)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Bills Included:</span>
                    <span>{monthlyBills.length} months</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Days Outstanding:</span>
                    <span>{account.daysOutstanding} days</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="include-payment-options" 
              checked={includePaymentOptions}
              onCheckedChange={(checked) => setIncludePaymentOptions(checked as boolean)}
            />
            <Label htmlFor="include-payment-options">Include payment options and instructions</Label>
          </div>

          <div>
            <Label htmlFor="additional-notes">Additional Notes (Optional)</Label>
            <Textarea 
              id="additional-notes"
              placeholder="Enter any additional information for the statement..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSendStatement} className="flex-1">
              <Send className="mr-2 h-4 w-4" />
              Send Statement
            </Button>
            <Button variant="outline" onClick={handlePrintStatement}>
              <Printer className="mr-2 h-4 w-4" />
              Preview & Print
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
